import type { LobbyTable } from '../types/table';

/**
 * Raw table shape as stored by consumers that keep the backend format
 * (e.g. apps/lobby), where limits are keyed by limit ID and use
 * `Min_Bet` / `Max_Bet` instead of `minBet` / `maxBet`.
 */
export type RawTable = Omit<LobbyTable, 'id' | 'limits'> & {
  tableId: number;
  limits: Record<string, Record<string, unknown> & { Min_Bet: number; Max_Bet: number }>;
};

/**
 * Resolve an array of table IDs through a lookup and convert the raw
 * backend table shape into `LobbyTable`-compatible objects.
 */
export const toLobbyTables = <T extends RawTable>(
  tableIds: Array<number | string>,
  tables: Record<string | number, T>
): LobbyTable[] => tableIds
    .map((id) => tables[id])
    .filter(Boolean)
    .map((t) => ({
      ...t,
      id: String(t.tableId),
      limits: Object.values(t.limits || {}).map((l) => ({
        ...l,
        minBet: l.Min_Bet,
        maxBet: l.Max_Bet,
      })),
    }));

/**
 * Extract numeric table IDs from an array of `LobbyTable` objects.
 */
export const toLobbyTableIds = (lobbyTables: LobbyTable[]): number[] => lobbyTables.map((t) => Number(t.id));
